/**
 * Who may start a chat with whom — the one copy of the `canInitiateChatWith`
 * role table that mobile and the backend share (SCH-1288, KTD5).
 *
 * Ported from mobile's `useChatPermissions` (`getChatRole`, the admin override,
 * missing-rule-allows-all, `'all'`, and the contextual `moderator` /
 * `section_parent` overlay) so the client's picker and the server's create and
 * add-member checks give the same verdict. Two steps:
 *   1. `resolveChatInitiationRule` turns the school's rules and the initiator's
 *      roles into the initiator's rule (once per initiator);
 *   2. `isChatRecipientAllowedByRole` / `canInitiateChatWithRecipient` judge one
 *      recipient against it.
 *
 * Out of scope here, owned by the caller:
 *   - `globalFeatures.chat.enabled` (mobile grants nothing when chat is off);
 *   - the student grade rule (`isChatGradeEligible` in `chat-visibility.ts`),
 *     which nobody skips, admins included;
 *   - fetching the contextual ID sets. A token the rule lists but whose set is
 *     not supplied admits nobody.
 */
import type { ChatRole, ChatRoleOrAll } from './global-features'

/**
 * The CONTEXTUAL recipient tokens: categories that resolve against the
 * initiator's own roster context rather than a school-wide role.
 *   - `moderator` — the effective moderators of the initiator's own spaces.
 *   - `section_parent` — the parents who share at least one section with the initiator.
 * Recipient-only: never a `getChatInitiatorRole` result, never an initiator key.
 */
export const CHAT_CONTEXTUAL_RECIPIENT_TOKENS = ['moderator', 'section_parent'] as const
export type ChatContextualRecipientToken = (typeof CHAT_CONTEXTUAL_RECIPIENT_TOKENS)[number]

/** The persona roles that key `canInitiateChatWith`, in priority order. */
export type ChatInitiatorRole = Exclude<ChatRole, ChatContextualRecipientToken>

/** The per-school role booleans the role table reads. `isStaff` must already be resolved by the caller. */
export interface ChatInitiationRoles {
  isStudent?: boolean
  isParent?: boolean
  isFaculty?: boolean
  isTeacher?: boolean
  isStaff?: boolean
  isAdmin?: boolean
}

/**
 * The stored rules, widened so a parsed `ChatInitiationRules` and a partial read
 * both fit. Recipient arrays may hold persona labels, `'admin'`, `'all'` and the
 * contextual tokens.
 */
export type ChatInitiationRulesInput = Readonly<Partial<Record<ChatInitiatorRole, readonly string[]>>>

/**
 * The caller-supplied contextual ID sets, keyed by token. Each set holds
 * `UsersOnSchools.publicId`s, the same ID space as `ChatInitiationRecipient.id`.
 */
export type ChatContextualRecipientSets = Readonly<Partial<Record<ChatContextualRecipientToken, ReadonlySet<string>>>>

export interface ChatInitiator {
  roles: ChatInitiationRoles | null | undefined
  /**
   * An admin grant from outside `roles` (mobile's Clerk admin flag; on the server,
   * an ACTIVE school admin or a super-admin). `roles.isAdmin === true` counts too.
   * Ignored when `roles` is missing, as on mobile.
   */
  isAdmin?: boolean
}

export interface ChatInitiationRecipient {
  /** `UsersOnSchools.publicId`; only the contextual tokens read it. */
  id?: string
  roles: ChatInitiationRoles | null | undefined
}

/**
 * The initiator's resolved rule.
 *   - `none` — no roles, or no chat role: may start nothing.
 *   - `admin` — admin override: may message anyone with a chat role.
 *   - `unrestricted` — the school has no `canInitiateChatWith`: may message anyone.
 *   - `rule` — the initiator's row of the table (`[]` when the row is missing).
 */
export type ChatInitiationRule =
  | { kind: 'none'; initiatorRole: null; allowedRecipientRoles: readonly ChatRoleOrAll[] }
  | { kind: 'admin'; initiatorRole: 'admin'; allowedRecipientRoles: readonly ChatRoleOrAll[] }
  | { kind: 'unrestricted'; initiatorRole: ChatInitiatorRole; allowedRecipientRoles: readonly ChatRoleOrAll[] }
  | { kind: 'rule'; initiatorRole: ChatInitiatorRole; allowedRecipientRoles: readonly ChatRoleOrAll[] }

/**
 * A member's chat role for the table. Admin is handled separately (override).
 * Priority: faculty (or teacher) > staff > parent > student.
 *
 * Mobile parity: the faculty branch requires the `isFaculty` key to be present,
 * so a roles object carrying `isTeacher: true` without any `isFaculty` key is not
 * faculty. Build roles objects with every key.
 */
export function getChatInitiatorRole(roles: ChatInitiationRoles | null | undefined): ChatInitiatorRole | null {
  if (!roles) return null
  if ('isFaculty' in roles && (roles.isFaculty || roles.isTeacher)) return 'faculty'
  if (roles.isStaff) return 'staff'
  if (roles.isParent) return 'parent'
  if (roles.isStudent) return 'student'
  return null
}

/** Whether a chat initiation rule permits starting any chat at all (mobile's `canCreateChats`). */
export function canCreateChatsUnderRule(rule: ChatInitiationRule): boolean {
  return rule.kind !== 'none' && rule.allowedRecipientRoles.length > 0
}

/** Resolve the initiator's row of the school's `canInitiateChatWith` table. */
export function resolveChatInitiationRule(
  canInitiateChatWith: ChatInitiationRulesInput | null | undefined,
  initiator: ChatInitiator
): ChatInitiationRule {
  const { roles } = initiator
  if (!roles) return { kind: 'none', initiatorRole: null, allowedRecipientRoles: [] }

  if (roles.isAdmin === true || initiator.isAdmin === true) {
    return { kind: 'admin', initiatorRole: 'admin', allowedRecipientRoles: ['all'] }
  }

  const initiatorRole = getChatInitiatorRole(roles)
  if (!initiatorRole) return { kind: 'none', initiatorRole: null, allowedRecipientRoles: [] }

  // Backwards compatibility: a school that never configured the table allows all.
  if (!canInitiateChatWith) return { kind: 'unrestricted', initiatorRole, allowedRecipientRoles: ['all'] }

  const allowed = (canInitiateChatWith[initiatorRole] ?? []) as readonly ChatRoleOrAll[]
  return { kind: 'rule', initiatorRole, allowedRecipientRoles: allowed }
}

/**
 * The persona path (mobile's `canMessageUser`): whether the recipient's
 * school-wide role admits them under the rule. A recipient with no chat role is
 * admitted only when the school has no table at all.
 */
export function isChatRecipientAllowedByRole(
  rule: ChatInitiationRule,
  recipientRoles: ChatInitiationRoles | null | undefined
): boolean {
  switch (rule.kind) {
    case 'none':
      return false
    case 'unrestricted':
      return true
    case 'admin':
      return getChatInitiatorRole(recipientRoles) !== null
    case 'rule': {
      const allowed = rule.allowedRecipientRoles
      if (allowed.includes('all')) return getChatInitiatorRole(recipientRoles) !== null
      const recipientRole = getChatInitiatorRole(recipientRoles)
      return recipientRole !== null && allowed.includes(recipientRole)
    }
  }
}

/** The contextual tokens the rule lists: the sets a caller needs to fetch or resolve. */
export function contextualTokensInRule(rule: ChatInitiationRule): ChatContextualRecipientToken[] {
  if (rule.kind !== 'rule') return []
  return CHAT_CONTEXTUAL_RECIPIENT_TOKENS.filter(token => rule.allowedRecipientRoles.includes(token))
}

/**
 * The full recipient gate (mobile's `canMessageCandidate`): the persona path, or
 * any contextual token the rule lists whose supplied set holds the recipient's
 * `id`. A listed token with no supplied set, or a recipient with no `id`, admits
 * nobody through that token.
 */
export function canInitiateChatWithRecipient(
  rule: ChatInitiationRule,
  recipient: ChatInitiationRecipient,
  contextualSets: ChatContextualRecipientSets = {}
): boolean {
  if (isChatRecipientAllowedByRole(rule, recipient.roles)) return true
  const { id } = recipient
  if (id === undefined) return false
  return contextualTokensInRule(rule).some(token => contextualSets[token]?.has(id) === true)
}
